import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useState,
    useRef,
    useEffect
} from 'react';
import { generateWordSearchRegex } from '../../functions/StringParsing';
import { Sort } from '../../enums/Sort'; // Ascending / Descending enum

/**
 * Represents the current state of the word search.
 */
export type WordSearch = {
    value?: string;
    regex?: RegExp;
    sort?: Sort; // undefined = unsorted
};

/**
 * Public handle for clearing this component externally via ref.
 */
export type WordSearchInputHandle = {
    clear: () => void;
};

/**
 * Converts a string into a regex or returns `undefined` if empty.
 *
 * @param s - The string to convert.
 * @returns A RegExp if the string is non-empty, otherwise `undefined`.
 */
const toRegexOrUndef = (s: string, ignoreFillerWords: boolean) =>
    s.trim().length ? generateWordSearchRegex(s, { ignoreFillerWords }) : undefined;

/**
 * Props for the `WordSearchInput` component.
 */
type WordSearchInputProps = {
    label?: string;
    defaultValue?: string;
    onChange?: (search: WordSearch) => void;
    placeholder?: string;
    clearable?: boolean;
    size?: 'sm' | 'lg';
    disabled?: boolean;
    wrapperClassName?: string;
    inputClassName?: string;
    buttonWidthCh?: number;
    uppercaseVisual?: boolean;

    sortable?: boolean;
    defaultSort?: Sort | undefined;   // initial sort
    sort?: Sort | undefined;          // controlled external sort
    filterFillerWords?: boolean;
};

/**
 * A text input component for performing word searches with optional sorting (Asc/Desc/None).
 *
 * @param props - Component props.
 * @param props.label - Optional label displayed above the input field.
 * @param props.defaultValue - The initial text value.
 * @param props.onChange - Callback fired when the search or sort changes.
 * @param props.placeholder - Text shown when the field is empty.
 * @param props.clearable - Whether a clear button should be shown.
 * @param props.size - Input size (`'sm'` or `'lg'`).
 * @param props.disabled - Whether the input and buttons are disabled.
 * @param props.wrapperClassName - Optional additional class for the outer wrapper.
 * @param props.inputClassName - Optional class for the `<input>` element.
 * @param props.buttonWidthCh - Max width in character units.
 * @param props.uppercaseVisual - If true, text is displayed in uppercase.
 * @param props.sortable - Whether sorting (Asc/Desc/None) is enabled.
 * @param props.defaultSort - Default initial sort value.
 * @param props.sort - Controlled external sort value.
 * @param props.filterFillerWords - ignore filler word filtering value.
 * @param ref - A ref exposing a `.clear()` method.
 * @returns The rendered `WordSearchInput` React component.
 */
const WordSearchInput = forwardRef<WordSearchInputHandle, WordSearchInputProps>(({
    label,
    defaultValue = '',
    onChange,
    placeholder = 'Filter…',
    clearable = true,
    size,
    disabled,
    wrapperClassName,
    inputClassName,
    buttonWidthCh,
    uppercaseVisual = false,
    sortable = false,
    defaultSort = undefined,
    sort: externalSort,
    filterFillerWords = true,
}, ref) => {
    const [value, setValue] = useState<string>(defaultValue);
    const [sort, setSort] = useState<Sort | undefined>(defaultSort);
    const didEmitInitial = useRef(false);

    const groupSize = size === 'sm' ? 'input-group-sm' : size === 'lg' ? 'input-group-lg' : '';
    const inputSize = size === 'sm' ? 'form-control-sm' : size === 'lg' ? 'form-control-lg' : '';

    /**
     * Keeps the internal sort in sync with the external `sort` prop.
     */
    useEffect(() => {
        if (sortable && externalSort !== undefined && externalSort !== sort) {
            setSort(externalSort);
        }
        if (sortable && externalSort === undefined && sort !== undefined) {
            setSort(undefined);
        }
    }, [externalSort, sortable, sort]);

    /**
     * Cycles through 3 sort states: Asc → Desc → None → Asc.
     *
     * @returns void
     */
    const cycleSort = useCallback(() => {
        if (!sortable || disabled) return;
        const next =
            sort === Sort.Ascending
                ? Sort.Descending
                : sort === Sort.Descending
                ? undefined
                : Sort.Ascending;
        setSort(next);
        onChange?.({ value, regex: toRegexOrUndef(value, filterFillerWords), sort: next });
    }, [sortable, disabled, sort, value, onChange]);

    /** Sort glyph display for the label. */
    const sortGlyph = !sortable
        ? ''
        : sort === undefined
        ? '⮃'
        : sort === Sort.Ascending
        ? '⭡'
        : '⭣';

    /**
     * Emits an updated WordSearch object when the input value changes.
     *
     * @param next - The new string entered by the user.
     */
    const emit = useCallback(
        (next: string) => {
            onChange?.({
                value: next || undefined,
                regex: toRegexOrUndef(next, filterFillerWords),
                sort: sortable ? sort : undefined
            });
        },
        [onChange, sort, sortable]
    );

    /**
     * Handles text input changes.
     *
     * @param e - The change event from the input.
     */
    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const next = e.target.value;
        setValue(next);
        emit(next);
    };

    /**
     * Clears the text and resets the sort value.
     */
    const handleClear = useCallback(() => {
        setValue('');
        setSort(undefined);
        onChange?.({ value: undefined, regex: undefined, sort: undefined });
    }, [onChange]);

    useImperativeHandle(ref, () => ({ clear: handleClear }), [handleClear]);

    /**
     * Emits the initial search/sort state once when mounted.
     */
    useEffect(() => {
        if (!sortable || didEmitInitial.current) return;
        didEmitInitial.current = true;
        onChange?.({ value, regex: toRegexOrUndef(value, filterFillerWords), sort });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Handles keyboard input on the label (Enter/Space toggles sort).
     *
     * @param e - The keyboard event.
     */
    const onLabelKeyDown: React.KeyboardEventHandler<HTMLLabelElement> = (e) => {
        if (!sortable || disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            cycleSort();
        }
    };

    return (
        <div className={['position-relative', wrapperClassName].filter(Boolean).join(' ')}>
            {label && (
                <label
                    className='form-label fw-semibold d-block mb-1'
                    onClick={cycleSort}
                    onKeyDown={onLabelKeyDown}
                    tabIndex={sortable && !disabled ? 0 : undefined}
                    style={sortable ? { cursor: 'pointer' } : undefined}
                    aria-disabled={disabled || !sortable || undefined}
                    aria-label={
                        sortable
                            ? `${label} sort ${
                                  sort === undefined
                                      ? 'none'
                                      : sort === Sort.Ascending
                                      ? 'ascending'
                                      : 'descending'
                              }`
                            : undefined
                    }
                >
                    {label}
                    {sortable && <span aria-hidden='true'> {sortGlyph}</span>}
                </label>
            )}

            <div
                className={['input-group', groupSize].filter(Boolean).join(' ')}
                style={{
                    width: '100%',
                    ...(buttonWidthCh ? { maxWidth: `${buttonWidthCh}ch` } : {})
                }}
            >
                <input
                    type='text'
                    className={[
                        'form-control',
                        'btn-form-select',
                        'btn-form-focus',
                        inputSize,
                        inputClassName
                    ].filter(Boolean).join(' ')}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleChange}
                    disabled={disabled}
                    style={uppercaseVisual ? { textTransform: 'uppercase' } : undefined}
                    aria-label={label || 'Word filter'}
                />

                {clearable && (
                    <button
                        type='button'
                        className='btn btn-outline-secondary btn-clear-neutral border-start-0 rounded-start-0'
                        onClick={handleClear}
                        disabled={disabled || (!value && sort === undefined)}
                        title='Clear'
                    >
                        Clear
                    </button>
                )}
            </div>
        </div>
    );
});

WordSearchInput.displayName = 'WordSearchInput';
export default WordSearchInput;
